/*
  This code is the intellectual property of Bulb Inc. and other software publishers;
  it may not be altered, copied or disclosed without prior written approval.
  Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import { EventEmitter, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ErrorService } from '../../common/error.service';
import { UppyComponentEvent, UppyComponentEventStatus } from './types';
import {
  buildOnCompleteEvent, buildOnErrorEvent, buildOnSuccessEvent,
} from './uppy-dashboard/uppy-dashboard-utilities';

// Creates an event emitter that emits events that correspond with Uppy events.
@Injectable({
  providedIn: 'root',
})
export class AssetUploadEventService {

  public readonly uppyEventEmitter: EventEmitter<UppyComponentEvent> = new EventEmitter<UppyComponentEvent>();

  /**
   * Event handling for opening embed modal.
   */
  openEmbedModal: Subject<CustomEvent> = new Subject();

  constructor(
    private errorSvc: ErrorService,
  ) { }

  /**
   * Handles the on-error event emitted from the Uppy Dashboard Component.
   * Emits an event about the failure and sends the error to sentry.
   *
   * @param response from the on-error event.
   */
  public handleOnError(response: any) {
    this.uppyEventEmitter.emit(buildOnErrorEvent(response));
    this.errorSvc.captureErrorMessage(
      'Error when uploading to the Companion App from Uppy Dashboard.',
      response,
    );
  }

  /**
   * Handles the on-complete event emitted from the Uppy Dashboard Component.
   * Emits an event about the success/failure/partial success uploads.
   * If all uploads succeeded, it will automatically close the modal after short delay.
   *
   * @param response from the on-error event.
   */
  public handleOnComplete(response: any) {
    this.uppyEventEmitter.emit(buildOnCompleteEvent(response));
  }

  public handleOnSuccess(response: any) {
    this.uppyEventEmitter.emit(buildOnSuccessEvent(UppyComponentEventStatus.SUCCESS, response));
  }

  public handleOnProcessingSuccess(response: any, uploadState?: any, contentBlockData?: any) {
    this.uppyEventEmitter.emit(buildOnSuccessEvent(UppyComponentEventStatus.PROCESSING_SUCCESS, response, uploadState, contentBlockData));
  }

  /**
   * Used to capture and report errors that occur during the http call to /companion/process. Upload to
   * companion app has completed, this captures the errors that happen during companionUpload
   * @param err Error that occurred
   */
  public handleOnProcessingFailure(err: any) {
    this.uppyEventEmitter.emit(buildOnSuccessEvent(UppyComponentEventStatus.PROCESSING_FAILURE, err));
    this.errorSvc.captureErrorMessage('Error when calling /companion/process', err);
  }
}
